import { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Btn, Spinner } from '../../components/ui';
import { adminAPI } from '../../api';

const BLANK_SCHOOL = { name: '', district: '', grade_low: '', grade_high: '' };

export default function SchoolsTab({ options, onChanged }) {
  const [districts, setDistricts] = useState([]);
  const [schools, setSchools]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [editing, setEditing]     = useState(null);
  const [newDistrict, setNewDistrict] = useState('');
  const [saving, setSaving]       = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([adminAPI.districts(), adminAPI.schools()])
      .then(([d, s]) => { setDistricts(d.data); setSchools(s.data); })
      .catch(err => { console.error(err); toast.error('Could not load schools.'); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const addDistrict = async () => {
    if (!newDistrict.trim()) return;
    try {
      await adminAPI.createDistrict({ name: newDistrict.trim() });
      toast.success('District added.');
      setNewDistrict('');
      load(); onChanged?.();
    } catch { toast.error('Could not add that district.'); }
  };

  const saveSchool = async () => {
    if (!editing.name || !editing.district) {
      toast.error('A school needs a name and a district.');
      return;
    }
    setSaving(true);
    try {
      if (editing.id) await adminAPI.updateSchool(editing.id, editing);
      else            await adminAPI.createSchool(editing);
      toast.success(editing.id ? 'School updated.' : 'School added.');
      setEditing(null);
      load(); onChanged?.();
    } catch (err) {
      const d = err?.response?.data;
      toast.error((d && Object.values(d).flat().join(' ')) || 'Could not save that school.');
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div style={{ display:'flex', justifyContent:'center', padding:'32px 0' }}><Spinner /></div>;
  }

  return (
    <>
      <div className="adm-card">
        <div className="adm-card-title">Districts</div>
        {districts.map(d => (
          <div key={d.id} className="adm-row">
            <span>{d.name}</span>
            <span className="adm-row-n">{d.school_count} school{d.school_count === 1 ? '' : 's'}</span>
          </div>
        ))}
        <div className="adm-toolbar" style={{ marginTop: 10 }}>
          <input className="fi" placeholder="New district name…" value={newDistrict}
                 onChange={e => setNewDistrict(e.target.value)}
                 onKeyDown={e => e.key === 'Enter' && addDistrict()} />
          <button className="adm-add" onClick={addDistrict}>Add</button>
        </div>
      </div>

      <Btn variant="blue" onClick={() => setEditing({ ...BLANK_SCHOOL })} style={{ marginBottom: 14 }}>
        Add school
      </Btn>

      {schools.length === 0 ? (
        <div className="dash-empty">No schools yet. Add a district, then a school.</div>
      ) : schools.map(s => (
        <div key={s.id} className="adm-user">
          <div className="adm-user-main">
            <div className="adm-user-name">{s.name}</div>
            <div className="adm-user-sub">{s.district_name} · {s.grade_range || 'No grade range set'}</div>
            <div className="adm-user-meta">
              {s.staff_count} staff{s.focus_bucket ? ` · focus: ${s.focus_bucket.replace(/_/g, ' ')}` : ''}
            </div>
          </div>
          <div className="adm-user-actions">
            <button className="adm-link" onClick={() => setEditing({ ...s })}>Edit</button>
          </div>
        </div>
      ))}

      {editing && (
        <div className="sheet-backdrop" onClick={() => setEditing(null)}>
          <div className="sheet" onClick={e => e.stopPropagation()}>
            <div className="sheet-head">
              <div className="sheet-title">{editing.id ? 'Edit school' : 'Add school'}</div>
              <button className="sheet-close" onClick={() => setEditing(null)}>✕</button>
            </div>

            <div className="sheet-body">
              <div className="sheet-lbl">School name</div>
              <input className="fi" value={editing.name}
                     onChange={e => setEditing({ ...editing, name: e.target.value })} />

              <div className="sheet-lbl">District</div>
              <select className="fi" value={editing.district || ''}
                      onChange={e => setEditing({ ...editing, district: e.target.value })}>
                <option value="">Select district…</option>
                {districts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>

              <div className="adm-field-row">
                <div style={{ flex: 1 }}>
                  <div className="sheet-lbl">Lowest grade</div>
                  <select className="fi" value={editing.grade_low || ''}
                          onChange={e => setEditing({ ...editing, grade_low: e.target.value })}>
                    <option value="">—</option>
                    {options?.grades.map(g => <option key={g}>{g}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <div className="sheet-lbl">Highest grade</div>
                  <select className="fi" value={editing.grade_high || ''}
                          onChange={e => setEditing({ ...editing, grade_high: e.target.value })}>
                    <option value="">—</option>
                    {options?.grades.map(g => <option key={g}>{g}</option>)}
                  </select>
                </div>
              </div>
              <div className="adm-hint" style={{ marginTop: 6 }}>
                The grade range is shown against the school when an observation is started.
              </div>
            </div>

            <div className="sheet-foot">
              <Btn variant="dark" onClick={saveSchool} loading={saving}>
                {editing.id ? 'Save changes' : 'Add school'}
              </Btn>
              <button className="sheet-cancel" onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
